import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Handshake, Store, Percent, X, Plus, CalendarClock, Building2, CreditCard,
         AlertTriangle, CheckCircle2 } from 'lucide-react';
import SectionHelp from '@/components/help/SectionHelp';
import HelpTooltip from '@/components/help/HelpTooltip';
import { usePrompt } from '@/components/admin/PromptDialog';

// S134 (3/N) — Socios de negocio. Es la pantalla que faltaba: sin ella no se
// podía dar de alta un solo socio, y sin socios la Cobranza no tenía qué
// enseñar.
//
// Las dos figuras se explican en la propia pantalla, no en un manual aparte:
// elegir mal la figura no se puede deshacer (un socio no cambia de tipo), así
// que la explicación tiene que estar delante en el momento de elegir.

const TARJETA = 'border rounded-lg p-4 bg-white';

// La explicación de las dos figuras vive AQUÍ, no en la respuesta del servidor.
// Lo cazó el guardia de render: con la API caída o lenta, el recuadro se pintaba
// vacío justo en la decisión que no se deshace, y la persona elegía a ciegas.
// Esto es texto de producto, no un dato.
const FIGURAS = {
  reseller: 'Vende CON NUESTRA MARCA. Nosotros le cobramos al cliente y después le '
    + 'pagamos su comisión: un porcentaje de la primera venta y otro de cada mes siguiente.',
  partner: 'Vende CON LA SUYA. Le vendemos licencias con un mínimo forzoso: se le '
    + 'cobran las activas o el mínimo, lo que sea mayor. Los clientes los da de alta '
    + 'él (no se le ceden cuentas de CavernicolApp). Puede tener precio fijo o '
    + 'escalonado. Lo que él le cobre a su cliente es asunto suyo.',
  no_se_cambia: 'Un socio no cambia de figura: no pasa de reseller a partner ni al '
    + 'revés. Si el trato cambia, se cierra y se abre uno nuevo.',
};

const VERTICALES_CLIENTE = [
  'beauty', 'health', 'fitness', 'education', 'automotive', 'real_estate',
  'professional_services', 'restaurant', 'hotel', 'workshop', 'spa', 'clinic',
  'generic',
];

/** Unitario según peldaños (misma regla que el backend). */
function unitarioEscalonado(cantidad, escalones) {
  const q = Math.max(Number(cantidad) || 0, 1);
  const orden = [...(escalones || [])]
    .map((e) => ({ desde: Number(e.desde) || 0, precio: Number(e.precio) || 0 }))
    .filter((e) => e.desde >= 1 && e.precio > 0)
    .sort((a, b) => a.desde - b.desde);
  if (!orden.length) return 0;
  let p = orden[0].precio;
  for (const e of orden) {
    if (q >= e.desde) p = e.precio;
  }
  return p;
}

/** Sugerencia de referencia según el tipo de pago (fácil de identificar en el banco). */
export function sugerenciaReferenciaPago({ tipo, socioNombre, periodo, tenantNombre }) {
  const nombre = (socioNombre || 'partner').trim() || 'partner';
  const t = (tipo || 'licencias').toLowerCase();
  if (t === 'ingreso_partner' || t === 'ingreso' || t === 'nuevo_partner') {
    return `ingreso ${nombre}`.slice(0, 120);
  }
  if (t === 'nuevo_tenant' || t === 'tenant_nuevo' || t === 'alta_cliente') {
    const extra = (tenantNombre || '').trim();
    const base = `nuevo tenant ${nombre}`;
    return (extra ? `${base} ${extra}` : base).slice(0, 120);
  }
  if (t === 'comision' || t === 'liquidacion') {
    return `pago comision ${nombre} ${(periodo || '').trim()}`.trim().slice(0, 120);
  }
  return `licencias ${(periodo || '').trim()} ${nombre}`.trim().slice(0, 120);
}

function tipoReferenciaCargo(cargo, cargosPrevios, socio) {
  const pagadosAntes = (cargosPrevios || []).filter(
    (c) => c.id !== cargo.id && c.estado === 'paid',
  ).length;
  if (pagadosAntes === 0 && (socio?.licencias_activas || 0) === 0) {
    return 'ingreso_partner';
  }
  if (pagadosAntes === 0) return 'ingreso_partner';
  return 'licencias';
}

function Ayuda({ children }) {
  return (
    <div className="border-l-4 border-slate-300 bg-slate-50 pl-3 py-2 text-sm text-slate-700">
      {children}
    </div>
  );
}

export function FormularioSocio({ inicial, sugerido, onGuardar, onCancelar, ocupado }) {
  const editando = !!inicial?.id;
  const [tipo, setTipo] = useState(inicial?.tipo || '');
  const escalonesIni = inicial?.escalones || inicial?.price_tiers
    || sugerido?.escalones
    || [{ desde: 1, precio: '100' }, { desde: 5, precio: '70' },
        { desde: 15, precio: '50' }, { desde: 35, precio: '35' }];
  const [f, setF] = useState({
    name: inicial?.nombre || '',
    legal_name: inicial?.razon_social || '',
    tax_id: inicial?.rfc || '',
    country: inicial?.pais || 'MX',
    contact_name: inicial?.contacto || '',
    contact_email: inicial?.correo || '',
    contact_phone: inicial?.telefono || '',
    notes: '',
    commission_first_percent: inicial?.comision_primera_venta || '',
    commission_recurring_percent: inicial?.comision_meses_siguientes || '',
    min_licenses: inicial?.minimo ?? sugerido?.minimo ?? 5,
    price_per_license: inicial?.precio_licencia || sugerido?.precio || '35',
    license_currency: inicial?.moneda || (inicial?.pais === 'MX' ? 'MXN' : 'USD'),
    forma_de_cobro: inicial?.forma_de_cobro || inicial?.cuenta_stripe || '',
    pricing_model: inicial?.modelo_precio || inicial?.pricing_model || 'fijo',
    price_tiers: Array.isArray(escalonesIni)
      ? escalonesIni.map((e) => ({ desde: String(e.desde), precio: String(e.precio) }))
      : [],
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setEscalon = (idx, campo, valor) => {
    const next = f.price_tiers.map((row, i) => (
      i === idx ? { ...row, [campo]: valor } : row
    ));
    setF({ ...f, price_tiers: next });
  };
  const minN = Math.max(1, Number(f.min_licenses) || 1);
  const unitarioPreview = f.pricing_model === 'escalonado'
    ? unitarioEscalonado(minN, f.price_tiers)
    : Number(f.price_per_license) || 0;
  const totalPreview = unitarioPreview * minN;

  // S136.2 — el catálogo viene del servidor porque la regla de qué empresa
  // factura vive allá. Copiarla aquí sería tener dos verdades: el día que una
  // cambie, la pantalla prometería una cosa y el cobro haría otra.
  const [paises, setPaises] = useState([]);
  useEffect(() => {
    api.get('/admin/socios/catalogo/paises')
      .then(({ data }) => setPaises(data.paises || []))
      .catch(() => setPaises([]));
  }, []);
  const paisElegido = paises.find((p) => p.codigo === f.country);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-start justify-center p-3 sm:p-6 overflow-y-auto z-50">
      {/* El alto máximo y el scroll van en el PANEL, no solo en el fondo: así
          la ventana se desplaza dentro de sí misma y el botón de guardar
          siempre se alcanza, sin depender de que el contenedor de atrás no la
          recorte. Antes se cortaba y no se veía ni el botón.

          2026-08-07 — el dueño reportó que seguía viéndose apretada. El scroll
          funcionaba, pero `max-w-2xl` son 672 px para un formulario de DOS
          columnas con datos fiscales y precios: cada campo quedaba a media
          caja y había que desplazarse por todo. Se ensancha a `max-w-5xl` y
          se reduce el margen exterior en pantallas chicas, que es lo que de
          verdad se sentía «cortado». */}
      <div className="bg-white rounded-lg p-5 sm:p-6 w-full max-w-5xl space-y-4
                      max-h-[calc(100vh-2rem)] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">
            {editando ? `Editar «${inicial.nombre}»` : 'Dar de alta un socio'}
          </h3>
          <Button variant="ghost" size="sm" onClick={onCancelar}><X className="w-4 h-4" /></Button>
        </div>

        {!editando && (
          <>
            <Ayuda>
              <b>Esta decisión no se deshace.</b> {FIGURAS.no_se_cambia}
            </Ayuda>
            <div className="grid md:grid-cols-2 gap-3">
              <button type="button" onClick={() => setTipo('reseller')}
                      className={`text-left ${TARJETA} ${tipo === 'reseller' ? 'ring-2 ring-indigo-500' : ''}`}>
                <div className="flex items-center gap-2 font-medium">
                  <Percent className="w-4 h-4 text-indigo-600" /> Reseller
                </div>
                <p className="text-sm text-slate-600 mt-1">{FIGURAS.reseller}</p>
              </button>
              <button type="button" onClick={() => setTipo('partner')}
                      className={`text-left ${TARJETA} ${tipo === 'partner' ? 'ring-2 ring-emerald-500' : ''}`}>
                <div className="flex items-center gap-2 font-medium">
                  <Store className="w-4 h-4 text-emerald-600" /> Partner de marca blanca
                </div>
                <p className="text-sm text-slate-600 mt-1">{FIGURAS.partner}</p>
              </button>
            </div>
          </>
        )}

        {(tipo || editando) && (
          <div className="space-y-3">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500">Nombre del socio</label>
                <Input value={f.name} onChange={set('name')} placeholder="Grupo Comercial del Norte" />
              </div>
              <div>
                <label className="text-xs text-slate-500 flex items-center gap-1">
                  País
                  <HelpTooltip text={'Decide con cuál de nuestras dos empresas se le factura: México va por la SAPI de CV con CFDI, el resto del mundo por la LLC con invoice.'} />
                </label>
                <select value={f.country} onChange={(e) => {
                          const pais = e.target.value;
                          const fila = paises.find((p) => p.codigo === pais);
                          const sugerida = fila?.moneda_base;
                          setF((prev) => ({
                            ...prev,
                            country: pais,
                            license_currency: sugerida || prev.license_currency,
                          }));
                        }}
                        data-testid="socio-pais"
                        className="w-full px-3 py-2 text-sm border border-[#E2E8F0] dark:border-white/10 rounded-md bg-transparent">
                  {paises.length === 0 && <option value={f.country}>{f.country}</option>}
                  {paises.map((p) => (
                    <option key={p.codigo} value={p.codigo}>{p.nombre}</option>
                  ))}
                </select>
                {/* La consecuencia del país se lee AQUÍ, antes de guardar.
                    Elegir mal manda la factura por la empresa equivocada y con
                    el documento equivocado; eso no puede depender de que
                    alguien se acuerde de la regla. */}
                {paisElegido && (
                  <p className="text-[11px] mt-1 leading-snug text-[#0F172A] dark:text-inherit
                                bg-sky-50 dark:bg-sky-500/10 border border-sky-200 rounded p-2">
                    Le factura <strong>{paisElegido.empresa}</strong> y recibe{' '}
                    <strong>{paisElegido.documento}</strong>. Se le cobra con la cuenta de
                    Stripe de esa empresa.
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs text-slate-500 flex items-center gap-1">
                  Razón social (para facturarle)
                  <HelpTooltip text={'El nombre legal con el que va en la factura. Puede no parecerse al nombre comercial con el que lo conoces.'} />
                </label>
                <Input value={f.legal_name} onChange={set('legal_name')} />
              </div>
              <div>
                <label className="text-xs text-slate-500 flex items-center gap-1">
                  RFC / Tax ID
                  <HelpTooltip text={'Su identificador fiscal. Sin esto no se le puede facturar, aunque el alta sí se puede guardar y completarlo después.'} />
                </label>
                <Input value={f.tax_id} onChange={set('tax_id')} />
              </div>
              <div>
                <label className="text-xs text-slate-500 flex items-center gap-1">
                  Persona de contacto
                  <HelpTooltip text={'A quién le hablas cuando hay que cobrar o resolver algo. No es un usuario del sistema todavía.'} />
                </label>
                <Input value={f.contact_name} onChange={set('contact_name')} />
              </div>
              <div>
                <label className="text-xs text-slate-500 flex items-center gap-1">
                  Correo
                  <HelpTooltip text={'También es el acceso al portal del socio (/socio), salvo que desactives crear portal. Debe ser único en el sistema.'} />
                </label>
                <Input value={f.contact_email} onChange={set('contact_email')}
                       placeholder="contacto@partner.com" data-testid="socio-correo" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Teléfono</label>
                <Input value={f.contact_phone} onChange={set('contact_phone')} />
              </div>
            </div>

            {(tipo || inicial?.tipo) === 'reseller' ? (
              <div className="border rounded-lg p-3 bg-indigo-50/40 space-y-2">
                <div className="text-sm font-medium">Lo que le vamos a pagar</div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-500 flex items-center gap-1">
                  % de la primera venta
                  <HelpTooltip text={'Lo que se lleva del PRIMER pago de cada cliente que traiga. Suele ser el más alto: es el premio por conseguirlo.'} />
                </label>
                    <Input value={f.commission_first_percent}
                           onChange={set('commission_first_percent')} placeholder="30" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 flex items-center gap-1">
                  % de cada mes siguiente
                  <HelpTooltip text={'Lo que se lleva de cada pago posterior mientras el cliente siga vivo. Se calcula sobre el pago completo, así que sube solo si el cliente sube de plan.'} />
                </label>
                    <Input value={f.commission_recurring_percent}
                           onChange={set('commission_recurring_percent')} placeholder="10" />
                  </div>
                </div>
                <p className="text-xs text-slate-600">
                  Se aplican sobre el pago completo del mes, así que los upgrades y
                  los add-ons comisionan solos. Si el cliente se cae en los primeros
                  meses, se recupera parte de lo pagado — la escalera está en Cobranza.
                </p>
              </div>
            ) : (
              <div className="border rounded-lg p-3 bg-emerald-50/40 space-y-3">
                <div className="text-sm font-medium">Lo que le vamos a cobrar (CavernicolApp define el precio)</div>
                <p className="text-xs text-slate-600">
                  Él da de alta <b>sus</b> clientes (ahora o después). Siempre paga al
                  menos el mínimo de licencias, aunque todavía no tenga negocios.
                  Los paquetes de mensajes y voz de cada tenant se adquieren aparte.
                </p>

                <div className="grid md:grid-cols-2 gap-2">
                  <button type="button"
                          onClick={() => setF({ ...f, pricing_model: 'fijo' })}
                          className={`text-left ${TARJETA} ${f.pricing_model === 'fijo' ? 'ring-2 ring-emerald-500' : ''}`}>
                    <div className="font-medium text-sm">Precio fijo</div>
                    <p className="text-xs text-slate-600 mt-1">
                      Un solo precio por licencia × máximo(activas, mínimo).
                      Ej.: mínimo 5 × 35 = 175 {f.license_currency || 'USD'}/mes.
                    </p>
                  </button>
                  <button type="button"
                          onClick={() => setF({ ...f, pricing_model: 'escalonado' })}
                          className={`text-left ${TARJETA} ${f.pricing_model === 'escalonado' ? 'ring-2 ring-emerald-500' : ''}`}>
                    <div className="font-medium text-sm">Precio escalonado</div>
                    <p className="text-xs text-slate-600 mt-1">
                      El unitario baja al vender más. Tú defines los peldaños
                      (ej. 1→100, 35→35). Ideal si arranca con un solo negocio.
                    </p>
                  </button>
                </div>

                <div className="grid md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-slate-500 flex items-center gap-1">
                      Mínimo de licencias
                      <HelpTooltip text={'Piso del trato: se cobra este número aunque tenga menos clientes (incluso cero). Es lo que paga desde el alta como partner.'} />
                    </label>
                    <Input value={f.min_licenses} onChange={set('min_licenses')} placeholder="5" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 flex items-center gap-1">
                      Moneda del trato
                      <HelpTooltip text={'En qué moneda le cobramos las licencias. México suele ser MXN (SAPI). Dólares y euros van por la LLC. Se puede cambiar después desde la ficha del socio.'} />
                    </label>
                    <select value={f.license_currency} onChange={set('license_currency')}
                            data-testid="socio-moneda"
                            className="w-full px-3 py-2 text-sm border border-[#E2E8F0] rounded-md bg-white">
                      <option value="MXN">Pesos mexicanos (MXN)</option>
                      <option value="USD">Dólares (USD)</option>
                      <option value="EUR">Euros (EUR)</option>
                    </select>
                  </div>
                  <div className="md:col-span-3">
                    <label className="text-xs text-slate-500 flex items-center gap-1">
                      Cuenta de Stripe donde cae el dinero
                      <HelpTooltip text={'Independiente de la moneda. Puedes cobrar en dólares y que entre en Stripe México (SAPI), o en pesos y que entre en la LLC. Tú lo decides.'} />
                    </label>
                    <select value={f.forma_de_cobro || ''}
                            onChange={set('forma_de_cobro')}
                            data-testid="socio-cuenta-stripe"
                            className="w-full px-3 py-2 text-sm border border-[#E2E8F0] rounded-md bg-white">
                      <option value="">Sugerida según la moneda (MXN→México, USD/EUR→LLC)</option>
                      <option value="stripe_mx">Stripe México (SAPI de CV) — CFDI</option>
                      <option value="stripe_intl">Stripe internacional (LLC) — invoice</option>
                    </select>
                    <p className="text-[11px] text-slate-500 mt-1">
                      Esto no cambia lo que le cobras: solo a qué empresa tuya le llega el dinero.
                    </p>
                  </div>
                  {f.pricing_model === 'fijo' && (
                    <div>
                      <label className="text-xs text-slate-500 flex items-center gap-1">
                        Precio por licencia
                        <HelpTooltip text={'Lo que NOSOTROS le cobramos a él por cada negocio. Lo que él le cobre a su cliente es asunto suyo.'} />
                      </label>
                      <Input value={f.price_per_license} onChange={set('price_per_license')} placeholder="35" />
                    </div>
                  )}
                </div>

                {f.pricing_model === 'escalonado' && (
                  <div className="space-y-2 border rounded p-2 bg-white/70">
                    <div className="text-xs font-medium text-slate-700">
                      Peldaños (desde N licencias cobrables → precio unitario)
                    </div>
                    {f.price_tiers.map((row, idx) => (
                      <div key={idx} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                        <div>
                          <label className="text-[11px] text-slate-500">Desde (licencias)</label>
                          <Input value={row.desde}
                                 onChange={(e) => setEscalon(idx, 'desde', e.target.value)}
                                 placeholder="1" />
                        </div>
                        <div>
                          <label className="text-[11px] text-slate-500">Precio unitario</label>
                          <Input value={row.precio}
                                 onChange={(e) => setEscalon(idx, 'precio', e.target.value)}
                                 placeholder="100" />
                        </div>
                        <Button type="button" size="sm" variant="ghost"
                                disabled={f.price_tiers.length <= 1}
                                onClick={() => setF({
                                  ...f,
                                  price_tiers: f.price_tiers.filter((_, i) => i !== idx),
                                })}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" size="sm" variant="outline"
                            onClick={() => setF({
                              ...f,
                              price_tiers: [...f.price_tiers, { desde: '', precio: '' }],
                            })}>
                      <Plus className="w-3 h-3 mr-1" /> Peldaño
                    </Button>
                    <p className="text-[11px] text-slate-500">
                      El primer peldaño debe empezar en 1. Con N licencias se aplica
                      el peldaño con el mayor «desde» que no supere N.
                    </p>
                  </div>
                )}

                <div className="rounded border border-emerald-200 bg-white px-3 py-2 text-sm">
                  <b>Al alta (mínimo {minN}):</b>{' '}
                  {minN} × {unitarioPreview || '—'} {f.license_currency || 'USD'}
                  {' '}= <b>{Number.isFinite(totalPreview) ? totalPreview : '—'}</b>
                  {' '}{f.license_currency || 'USD'}/mes
                  {f.pricing_model === 'escalonado' && (
                    <span className="block text-xs text-slate-500 mt-1">
                      Si llega a 35 licencias cobrables: unitario ≈{' '}
                      {unitarioEscalonado(35, f.price_tiers) || '—'} {f.license_currency || 'USD'}.
                    </span>
                  )}
                </div>
              </div>
            )}

            <Textarea placeholder="Notas internas sobre el trato"
                      value={f.notes} onChange={set('notes')} />

            <div className="flex gap-2">
              <Button disabled={ocupado}
                      onClick={() => {
                        const payload = {
                          ...f,
                          kind: tipo || inicial?.tipo,
                          min_licenses: Number(f.min_licenses) || 1,
                        };
                        if (payload.pricing_model === 'escalonado') {
                          payload.price_tiers = (f.price_tiers || [])
                            .map((r) => ({
                              desde: Number(r.desde),
                              precio: String(r.precio).trim(),
                            }))
                            .filter((r) => r.desde >= 1 && r.precio);
                          // el backend recalcula price_per_license al mínimo
                          delete payload.price_per_license;
                        } else {
                          payload.price_tiers = null;
                        }
                        onGuardar(payload);
                      }}>
                {editando ? 'Guardar cambios' : 'Dar de alta'}
              </Button>
              <Button variant="ghost" onClick={onCancelar}>Cancelar</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * La bolsa de mensajes de WhatsApp de un socio: saldo, aviso de margen y los
 * bloques que se le pueden vender.
 *
 * Componente exportado y no JSX suelto dentro de la ficha por dos razones: el
 * arnés de render no hace clic, así que dentro de la ficha no se podría
 * comprobar nunca; y Grok trabaja esta misma pantalla, así que cuanto más
 * aislado, menos hay que tocar de lo suyo.
 */
export function BolsaDeMensajesDelSocio({ mensajes, razon, ocupado, onVender }) {
  if (mensajes == null) {
    return (
      <div className={TARJETA} data-testid="socio-bloques-mensajes">
        <div className="text-sm text-slate-500">Cargando la bolsa de mensajes…</div>
      </div>
    );
  }
  if (mensajes.error) {
    return (
      <div className={`${TARJETA} border-rose-200 bg-rose-50/40`} data-testid="socio-bloques-mensajes">
        <div className="text-sm font-medium text-rose-800">No se pudo cargar la bolsa</div>
        <p className="text-xs text-rose-700 mt-1">{mensajes.error}</p>
      </div>
    );
  }
  const freno = mensajes.freno_saliente || {};
  return (
    <div className={TARJETA} data-testid="socio-bloques-mensajes">
      <div className="text-sm font-medium mb-1">Mensajes de WhatsApp</div>
      <p className="text-xs text-slate-500 mb-2">{mensajes.aviso}</p>
      {freno.zona === 'excedente' && (
        <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded p-2 mb-2">
          <b>Excedente (deuda):</b> {freno.deuda_msgs?.toLocaleString?.('es-MX') || freno.deuda_msgs}
          {' '}msgs · tope 30 % de última compra = {freno.tope_excedente?.toLocaleString?.('es-MX')}.
          Los envíos siguen; conviene recargar.
        </div>
      )}
      {freno.zona === 'bloqueado' && (
        <div className="text-sm text-rose-900 bg-rose-50 border border-rose-200 rounded p-2 mb-2">
          <b>Envíos frenados.</b> Deuda {freno.deuda_msgs?.toLocaleString?.('es-MX')} &gt; tope{' '}
          {freno.tope_excedente?.toLocaleString?.('es-MX')}. Super Admin fue notificado para
          valorar traspaso si no recarga.
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 mb-3">
        {['entrante', 'saliente'].map((d) => {
          const b = mensajes.saldo?.[d] || {};
          return (
            <div key={d} className="border border-slate-200 rounded p-2">
              <div className="text-xs text-slate-500">
                {d === 'entrante' ? 'Le escriben' : 'Envía'}
              </div>
              <div className={`text-lg font-semibold tabular-nums ${b.excedido ? 'text-amber-600' : ''}`}>
                {(b.restante ?? 0).toLocaleString('es-MX')}
              </div>
              <div className="text-[11px] text-slate-500">
                {(b.gastado ?? 0).toLocaleString('es-MX')} usados de
                {' '}{(b.comprado ?? 0).toLocaleString('es-MX')}
              </div>
            </div>
          );
        })}
      </div>

      {/* El aviso que protege el margen: se vende por mensaje y Meta cobra por
          conversación, así que un socio de conversaciones cortas nos cuesta
          mucho más de lo que nos paga. */}
      {razon?.aviso && (
        <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
          <b>Ojo con este socio.</b> {razon.aviso}
        </div>
      )}

      <div className="text-xs text-slate-500 mb-1">Venderle un bloque:</div>
      <div className="flex flex-wrap gap-2">
        {(mensajes.bloques || []).map((b) => (
          <Button key={b.clave} size="sm" variant="outline" disabled={ocupado}
                  onClick={() => onVender(b)}>
            {b.mensajes.toLocaleString('es-MX')} {b.direccion} · ${b.precio_usd}
          </Button>
        ))}
      </div>
    </div>
  );
}


export default function Socios() {
  const { ask, ui: promptUi } = usePrompt();
  const [cargando, setCargando] = useState(true);
  const [datos, setDatos] = useState(null);
  const [formulario, setFormulario] = useState(null);   // null | {} | socio
  const [abierto, setAbierto] = useState(null);         // ficha del socio
  const [mensajes, setMensajes] = useState(null);       // su bolsa de WhatsApp
  const [razon, setRazon] = useState(null);             // mensajes por conversación
  const [disponibles, setDisponibles] = useState([]);
  const [cobro, setCobro] = useState(null);
  const [vendedores, setVendedores] = useState([]);
  const [ocupado, setOcupado] = useState(false);
  const [clienteNuevo, setClienteNuevo] = useState({
    name: '', vertical: 'beauty', owner_name: '', owner_email: '',
  });
  const [periodo, setPeriodo] = useState(() => {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [traspasos, setTraspasos] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const [lista, gente] = await Promise.all([
        api.get('/admin/socios'),
        api.get('/admin/socios/catalogo/vendedores'),
      ]);
      setDatos(lista.data);
      setVendedores(gente.data?.vendedores || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCargando(false); }
  }, []);

  const cargarTraspasos = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/socios/traspasos');
      setTraspasos(data);
    } catch (e) {
      setTraspasos({ error: formatApiError(e), traspasos: [] });
    }
  }, []);

  useEffect(() => { cargar(); cargarTraspasos(); }, [cargar, cargarTraspasos]);

  async function abrirFicha(id) {
    try {
      const [ficha, libres] = await Promise.all([
        api.get(`/admin/socios/${id}`),
        api.get(`/admin/socios/${id}/negocios-disponibles`),
      ]);
      setAbierto(ficha.data);
      setDisponibles(libres.data?.negocios || []);
      // El cobro se pide aparte a propósito: consulta a Stripe y puede tardar
      // o fallar. Si fuera parte de la misma llamada, un Stripe lento dejaría
      // la ficha entera sin abrir.
      setCobro(null);
      try {
        const c = await api.get(`/admin/socios/${id}/cobro`);
        setCobro(c.data);
      } catch (e) { setCobro({ error: formatApiError(e) }); }
      await cargarMensajes(id);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  // Aparte de la ficha, igual que el cobro: si fallara, la ficha tiene que
  // abrirse de todas formas.
  async function cargarMensajes(id) {
    setMensajes(null); setRazon(null);
    try {
      const { data } = await api.get(`/admin/socios/${id}/mensajes`);
      setMensajes(data);
      setRazon((data.razon || []).find((r) => r.partner_id === String(id)) || null);
    } catch (e) { setMensajes({ error: formatApiError(e) }); }
  }

  async function venderBloque(id, bloque) {
    if (!window.confirm(
      `Abonarle ${bloque.mensajes.toLocaleString('es-MX')} mensajes de `
      + `${bloque.direccion} por $${bloque.precio_usd} USD.\n\n`
      + 'Esto acredita los mensajes en su bolsa; el cobro se hace aparte.')) return;
    setOcupado(true);
    // Idempotencia: dos clics no son dos paquetes (backend dedupe por referencia).
    const referencia = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `compra-${id}-${bloque.clave}-${Date.now()}`;
    try {
      await api.post(`/admin/socios/${id}/mensajes/comprar`, {
        bloque: bloque.clave,
        referencia,
      });
      toast.success('Mensajes abonados');
      await cargarMensajes(id);
    } catch (e) { toast.error(formatApiError(e) || 'No se pudo abonar'); }
    finally { setOcupado(false); }
  }

  async function guardar(campos) {
    setOcupado(true);
    try {
      if (formulario?.id) {
        const { data } = await api.put(`/admin/socios/${formulario.id}`, {
          ...campos, crear_portal: undefined, portal_password: undefined,
        });
        toast.success(data.aviso);
      } else {
        const { data } = await api.post('/admin/socios', {
          ...campos,
          crear_portal: true,
          portal_email: campos.contact_email || undefined,
        });
        toast.success(data.aviso);
        if (data.credencial?.password_temporal) {
          await ask({
            title: 'Credencial del portal (cópiala ahora)',
            description: `Correo: ${data.credencial.email}\n\nContraseña temporal (no se vuelve a mostrar):`,
            defaultValue: data.credencial.password_temporal,
            confirmLabel: 'Listo, ya la copié',
            required: false,
          });
        }
      }
      setFormulario(null);
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setOcupado(false); }
  }

  async function asignar(tenantId, socioId) {
    setOcupado(true);
    try {
      const { data } = await api.post('/admin/socios/asignar',
                                      { tenant_id: tenantId, partner_id: socioId });
      toast.success(data.aviso);
      await abrirFicha(socioId);
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setOcupado(false); }
  }

  async function altaClientePartner(socioId) {
    if (!clienteNuevo.name?.trim()) {
      toast.error('Escribe el nombre del negocio del cliente.');
      return;
    }
    setOcupado(true);
    try {
      const body = {
        name: clienteNuevo.name.trim(),
        vertical: clienteNuevo.vertical || 'generic',
        timezone: 'America/Mexico_City',
      };
      if (clienteNuevo.owner_email?.trim() && clienteNuevo.owner_name?.trim()) {
        body.owner_email = clienteNuevo.owner_email.trim();
        body.owner_name = clienteNuevo.owner_name.trim();
      }
      const { data } = await api.post(`/admin/socios/${socioId}/clientes`, body);
      toast.success(data.aviso);
      setClienteNuevo({ name: '', vertical: 'beauty', owner_name: '', owner_email: '' });
      await abrirFicha(socioId);
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setOcupado(false); }
  }

  async function abrirCobro(socioId) {
    const medio = await ask({
      title: 'Medio de cobro del partner',
      description:
        'tarjeta → genera enlace Stripe y lo manda por correo/WhatsApp\n'
        + '(plantilla partner_card_setup; NO cobra al guardar)\n\n'
        + 'transferencia → solo deja el trato sin tarjeta (manual)\n\n'
        + 'Escribe «tarjeta» o «transferencia»:',
      defaultValue: 'tarjeta',
    });
    if (!medio) return;
    const m = medio.trim().toLowerCase();
    if (m !== 'tarjeta' && m !== 'transferencia') {
      toast.error('Escribe tarjeta o transferencia.');
      return;
    }
    setOcupado(true);
    try {
      const { data } = await api.post(`/admin/socios/${socioId}/cobro/abrir`, {
        medio: m,
        enviar_aviso: m === 'tarjeta',
      });
      toast.success(data.aviso || 'Cobro abierto');
      if (data.enlace_tarjeta) {
        try {
          await navigator.clipboard?.writeText(data.enlace_tarjeta);
          toast.message('Enlace de tarjeta copiado al portapapeles');
        } catch (_) { /* ignore */ }
        await ask({
          title: 'Enlace Stripe (registro de tarjeta)',
          description: 'También se intentó enviar por la plantilla de plataforma. Cópialo si hace falta:',
          defaultValue: data.enlace_tarjeta,
          confirmLabel: 'Cerrar',
          required: false,
        });
      }
      if (data.aviso_envio && !data.aviso_envio.ok) {
        const det = (data.aviso_envio.canales || [])
          .map((c) => `${c.canal}: ${c.detalle || (c.ok ? 'ok' : 'falló')}`)
          .join(' · ')
          || data.aviso_envio.saltado
          || 'sin detalle';
        toast.error(`Aviso al partner incompleto: ${det}`);
      }
      await abrirFicha(socioId);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setOcupado(false); }
  }

  async function reenviarEnlaceTarjeta(socioId) {
    setOcupado(true);
    try {
      const { data } = await api.post(
        `/admin/socios/${socioId}/cobro/reenviar-enlace-tarjeta`);
      toast.success(data.aviso || 'Enlace regenerado');
      if (data.enlace_tarjeta) {
        await ask({
          title: 'Enlace Stripe (copiar)',
          defaultValue: data.enlace_tarjeta,
          confirmLabel: 'Cerrar',
          required: false,
        });
      }
      await abrirFicha(socioId);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setOcupado(false); }
  }

  async function desasignarNegocio(tenantId, socioId, nombre) {
    if (!window.confirm(
      `Vas a QUITAR «${nombre}» de este partner.\n\n`
      + 'El negocio sigue vivo y pasa a ser venta directa de CavernicolApp.\n'
      + 'El partner deja de pagar esa licencia (Stripe se ajusta al mes siguiente).\n\n'
      + '¿Seguimos?')) return;
    setOcupado(true);
    try {
      const { data } = await api.post(
        `/admin/socios/${socioId}/negocios/${tenantId}/desasignar`);
      toast.success(data.aviso);
      await abrirFicha(socioId);
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setOcupado(false); }
  }

  async function cerrarCobro(socioId) {
    if (!window.confirm('Vas a dejar de cobrarle su licencia.\n\n'
        + 'El mes que ya pagó lo conserva completo; no se le vuelve a cobrar '
        + 'después de eso.\n\n¿Seguimos?')) return;
    setOcupado(true);
    try {
      const { data } = await api.post(`/admin/socios/${socioId}/cobro/cerrar`);
      toast.success(data.aviso);
      await abrirFicha(socioId);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setOcupado(false); }
  }

  async function cobrarLicencias(socioId) {
    setOcupado(true);
    try {
      const { data } = await api.post(`/admin/socios/${socioId}/cobrar-licencias`,
                                      { periodo });
      toast.success(data.aviso);
      await abrirFicha(socioId);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setOcupado(false); }
  }

  async function pagarCargo(cargo, socioId) {
    // Prioridad de producto: cobro con tarjeta. El registro manual es para
    // transferencias reales, no para saltarse Stripe.
    if (cobro && (cobro.prioridad_tarjeta || cobro.estado === 'sin_suscripcion' || !cobro.tiene_tarjeta)) {
      const ok = window.confirm(
        'PRIORIDAD: cobro automático con tarjeta.\n\n'
        + 'Este partner aún no tiene tarjeta / suscripción en Stripe.\n'
        + 'Lo correcto es «Abrir su cobro mensual» y que él ponga la tarjeta '
        + 'en el portal del socio.\n\n'
        + '¿Aun así vas a registrar un pago MANUAL (transferencia real)?');
      if (!ok) return;
    }
    const tipo = tipoReferenciaCargo(cargo, abierto?.cargos || [], abierto?.socio);
    const sugerida = sugerenciaReferenciaPago({
      tipo,
      socioNombre: abierto?.socio?.nombre || '',
      periodo: cargo.periodo,
    });
    const referencia = await ask({
      title: `Registrar pago de ${cargo.total} ${cargo.moneda}`,
      description: (tipo === 'ingreso_partner'
        ? 'Sugerencia (primer cobro del partner): deja o ajusta la referencia.\n'
        : 'Sugerencia (licencias del mes): deja o ajusta la referencia.\n')
        + 'Así lo identificas en el banco o en el estado de cuenta.',
      defaultValue: sugerida,
      placeholder: 'Referencia (transferencia, folio)',
    });
    if (!referencia) return;
    setOcupado(true);
    try {
      const { data } = await api.post(`/admin/socios/cargos/${cargo.id}/pagar`,
                                      { referencia: referencia.trim() });
      toast.success(data.aviso);
      await abrirFicha(socioId);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setOcupado(false); }
  }

  async function deshacerPagoManual(cargo, socioId) {
    const motivo = await ask({
      title: 'Deshacer pago manual',
      description:
        'El cargo queda anulado y el mes se puede cobrar por tarjeta.\n'
        + '¿Por qué se deshace?',
      multiline: true,
    });
    if (!motivo || motivo.trim().length < 3) return;
    setOcupado(true);
    try {
      const { data } = await api.post(
        `/admin/socios/cargos/${cargo.id}/deshacer-pago`,
        { motivo: motivo.trim() });
      toast.success(data.aviso);
      await abrirFicha(socioId);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setOcupado(false); }
  }

  if (cargando) return <div className="p-6 text-slate-500">Cargando los socios…</div>;

  const socios = datos?.socios || [];

  return (
    <div className="p-6 space-y-5 max-w-6xl" data-testid="socios">
      {promptUi}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Handshake className="w-6 h-6 text-indigo-600" />
          <h1 className="text-2xl font-semibold">Socios de negocio</h1>
        </div>
        <Button onClick={() => setFormulario({})}>
          <Plus className="w-4 h-4 mr-1" /> Dar de alta un socio
        </Button>
      </div>

      <SectionHelp
        module="socios"
        title="esta pantalla"
        description={'Aquí se dan de alta las personas o empresas que venden por '
          + 'nosotros. Al partner se le cobrará el mínimo de licencias desde el '
          + 'alta; sus clientes se crean como negocios nuevos (no se le ceden '
          + 'cuentas de CavernicolApp). Se puede pactar precio fijo o escalonado.'}
        whatItDoesNot={'no cobra ni paga sola, y no debe reasignar tenants Corp '
          + 'de la casa al partner: el partner da de alta clientes propios.'} />

      <Ayuda>
        <div><b>Reseller:</b> {FIGURAS.reseller}</div>
        <div className="mt-1"><b>Partner de marca blanca:</b> {FIGURAS.partner}</div>
        <div className="mt-1 text-slate-600">{FIGURAS.no_se_cambia}</div>
      </Ayuda>

      {/* Traspasos por impago — API ya existía; la UI faltaba (auditoría Super Admin). */}
      <div className={`${TARJETA} border-amber-200`} data-testid="traspasos-impago">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
          <h2 className="text-sm font-semibold flex items-center gap-1">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            Traspasos por impago
          </h2>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={ocupado}
                    onClick={async () => {
                      setOcupado(true);
                      try {
                        const { data } = await api.post('/admin/socios/traspasos/revisar');
                        toast.success(
                          data.propuestos?.length
                            ? `${data.propuestos.length} propuesto(s). ${data.nota || ''}`
                            : (data.nota || 'Revisión hecha: nadie nuevo propuesto.'),
                        );
                        await cargarTraspasos();
                      } catch (e) { toast.error(formatApiError(e)); }
                      finally { setOcupado(false); }
                    }}>
              Revisar impagos ahora
            </Button>
            <Button size="sm" variant="ghost" disabled={ocupado} onClick={cargarTraspasos}>
              Actualizar
            </Button>
          </div>
        </div>
        <p className="text-xs text-slate-600 mb-2">
          {traspasos?.regla
            || 'Tras 2 meses vencidos el sistema PROPONE; tú autorizas. Al autorizar, los tenants pasan a CavernicolApp y se cancela Stripe del partner.'}
        </p>
        {traspasos == null && (
          <p className="text-sm text-slate-500">Cargando traspasos…</p>
        )}
        {traspasos?.error && (
          <p className="text-sm text-rose-700">No se pudieron cargar: {traspasos.error}</p>
        )}
        {Array.isArray(traspasos?.traspasos) && traspasos.traspasos.length === 0 && (
          <p className="text-sm text-slate-500">Nadie propuesto ni traspasado en este momento.</p>
        )}
        {Array.isArray(traspasos?.traspasos) && traspasos.traspasos.length > 0 && (
          <div className="space-y-2">
            {traspasos.traspasos.map((t) => (
              <div key={t.id} className="border rounded p-2 text-sm flex flex-wrap items-center justify-between gap-2">
                <div>
                  <b>{t.socio}</b>
                  <span className="text-slate-500"> · {t.estado}</span>
                  {t.negocios_en_juego != null && (
                    <span className="text-slate-500"> · {t.negocios_en_juego} negocio(s)</span>
                  )}
                  {t.motivo && <div className="text-xs text-slate-600 mt-0.5">{t.motivo}</div>}
                </div>
                {t.estado === 'propuesto' && (
                  <div className="flex gap-1">
                    <Button size="sm" disabled={ocupado}
                            onClick={async () => {
                              const motivo = await ask({
                                title: 'Autorizar traspaso',
                                description:
                                  'Los clientes pasan a venta directa y se cancela el cobro del partner.\n'
                                  + 'Motivo (queda en el historial):',
                                defaultValue: `Impago — ${t.socio}`,
                                multiline: true,
                              });
                              if (!motivo || motivo.trim().length < 10) {
                                toast.error('El motivo debe tener al menos 10 caracteres.');
                                return;
                              }
                              setOcupado(true);
                              try {
                                const { data } = await api.post(
                                  `/admin/socios/${t.id}/traspaso/autorizar`,
                                  { motivo: motivo.trim() });
                                toast.success(data.aviso || 'Traspaso autorizado');
                                await cargarTraspasos();
                                await cargar();
                              } catch (e) { toast.error(formatApiError(e)); }
                              finally { setOcupado(false); }
                            }}>
                      Autorizar traspaso
                    </Button>
                    <Button size="sm" variant="outline" disabled={ocupado}
                            onClick={async () => {
                              const motivo = await ask({
                                title: 'Cancelar propuesta de traspaso',
                                description: 'El socio vuelve al corriente. ¿Por qué se cancela?',
                                defaultValue: 'Pago regularizado',
                              });
                              if (!motivo || motivo.trim().length < 3) return;
                              setOcupado(true);
                              try {
                                const { data } = await api.post(
                                  `/admin/socios/${t.id}/traspaso/cancelar-propuesta`,
                                  { motivo: motivo.trim() });
                                toast.success(data.aviso || 'Propuesta cancelada');
                                await cargarTraspasos();
                              } catch (e) { toast.error(formatApiError(e)); }
                              finally { setOcupado(false); }
                            }}>
                      Cancelar propuesta
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {socios.length === 0 ? (
        <div className="border rounded-lg p-6 bg-slate-50 text-slate-700 space-y-2">
          <div className="font-medium">Todavía no hay ningún socio dado de alta.</div>
          <p className="text-sm">
            Da de alta el primero con el botón de arriba. En cuanto tenga negocios
            asignados, sus comisiones o sus licencias empiezan a aparecer en
            <b> Cobranza</b>.
          </p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Socio</th>
                <th className="text-left px-3 py-2">Figura</th>
                <th className="text-right px-3 py-2">Negocios</th>
                <th className="text-left px-3 py-2">El trato</th>
                <th className="text-right px-3 py-2">Acción</th>
              </tr>
            </thead>
            <tbody>
              {socios.map((s) => (
                <tr key={s.id} className="border-t">
                  <td className="px-3 py-2">
                    <div className="font-medium">{s.nombre}</div>
                    <div className="text-xs text-slate-500">
                      {s.contacto || 'sin contacto'} · {s.correo || 'sin correo'}
                      {s.estado !== 'active' && (
                        <span className="ml-1 text-rose-700">· {s.estado}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {s.tipo === 'reseller' ? (
                      <span className="text-xs px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                        Reseller
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                        Marca blanca
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{s.negocios_activos}</td>
                  <td className="px-3 py-2 text-slate-600">
                    {s.tipo === 'reseller' ? (
                      <>Gana {s.comision_primera_venta}% la primera venta y {s.comision_meses_siguientes}% cada mes</>
                    ) : (
                      <>
                        {s.modelo_precio === 'escalonado' ? 'Escalonado' : 'Fijo'}
                        {' · '}mín. {s.minimo} × {s.precio_licencia} {s.moneda}
                        {' · '}
                        <b>se le cobran {s.se_le_cobrarian}</b> = {s.total_del_mes} {s.moneda}
                      </>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => abrirFicha(s.id)}>
                      Ver ficha
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {formulario && (
        <FormularioSocio inicial={formulario.id ? formulario : null}
                         sugerido={datos?.sugerido_partner}
                         onGuardar={guardar} ocupado={ocupado}
                         onCancelar={() => setFormulario(null)} />
      )}

      {abierto && (
        <div className="fixed inset-0 bg-black/40 flex items-start justify-center p-3 sm:p-6 overflow-y-auto z-50">
          <div className="bg-white rounded-lg p-5 w-full max-w-3xl space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h3 className="font-semibold">{abierto.socio.nombre}</h3>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline"
                        onClick={() => { setFormulario(abierto.socio); setAbierto(null); }}>
                  Editar
                </Button>
                {abierto.socio.correo && (
                  <Button size="sm" variant="outline" disabled={ocupado}
                          onClick={async () => {
                            const email = await ask({
                              title: 'Correo del portal del socio',
                              description: 'Si ya tiene credencial, la API fallará (no se duplica).',
                              defaultValue: abierto.socio.correo || '',
                            });
                            if (!email) return;
                            setOcupado(true);
                            try {
                              const { data } = await api.post(
                                `/admin/socios/${abierto.socio.id}/credencial`,
                                { email: email.trim() });
                              toast.success(data.aviso);
                              if (data.credencial?.password_temporal) {
                                await ask({
                                  title: 'Contraseña temporal (cópiala ahora)',
                                  defaultValue: data.credencial.password_temporal,
                                  confirmLabel: 'Listo',
                                  required: false,
                                });
                              }
                            } catch (e) { toast.error(formatApiError(e)); }
                            finally { setOcupado(false); }
                          }}>
                    Crear / reintentar portal
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => setAbierto(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {abierto.socio.login_url && (
              <div className={`${TARJETA} border-emerald-200 bg-emerald-50/40 space-y-2`}>
                <div className="text-sm font-medium">Acceso de su portal</div>
                <p className="text-xs text-slate-600">
                  El partner entra aquí (no al Super Admin). Cuando el DNS esté
                  verificado, esa dirección lleva su marca desde el login.
                </p>
                <div className="flex flex-wrap gap-2">
                  <a href={abierto.socio.login_url} target="_blank" rel="noreferrer"
                     className="inline-flex items-center h-8 px-3 text-sm rounded-md border bg-white hover:bg-slate-50">
                    Abrir {abierto.socio.login_url.replace('https://','')}
                  </a>
                  <Button size="sm" variant="outline" disabled={ocupado}
                          onClick={async () => {
                            setOcupado(true);
                            try {
                              const { data } = await api.post(
                                `/admin/socios/${abierto.socio.id}/dominio/comprobar`);
                              toast[data.verificado ? 'success' : 'message'](data.aviso);
                              setAbierto((a) => a ? {
                                ...a,
                                socio: { ...a.socio, dominio_estado: data.estado, login_url: data.login_url || a.socio.login_url },
                              } : a);
                            } catch (e) { toast.error(formatApiError(e)); }
                            finally { setOcupado(false); }
                          }}>
                    Comprobar DNS
                  </Button>
                </div>
                <p className="text-[11px] text-slate-500">
                  Estado del dominio: {abierto.socio.dominio_estado || 'sin configurar'}
                </p>
              </div>
            )}

            {abierto.socio.tipo === 'partner' && (
              <div className={`${TARJETA} ${cobro && !cobro.al_corriente ? 'border-rose-300 bg-rose-50/60' : 'border-indigo-200 bg-indigo-50/40'}`}
                   data-testid="cobro-del-socio">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <h4 className="text-sm font-medium flex items-center gap-1">
                    <CreditCard className="w-4 h-4" /> 1. Cobro automático con tarjeta (prioridad)
                    <HelpTooltip text={'Suscripción real en Stripe. Cuando le reponen la tarjeta, '
                      + 'Stripe consigue el número nuevo solo. Es el camino normal del partner.'} />
                  </h4>
                  <div className="flex gap-2 flex-wrap">
                    {/* No mostrar acciones de dinero mientras Stripe no respondió. */}
                    {cobro === null && (
                      <Button size="sm" disabled>Consultando…</Button>
                    )}
                    {cobro && !cobro.error && cobro.estado === 'sin_suscripcion' && (
                      <Button size="sm" disabled={ocupado}
                              onClick={() => abrirCobro(abierto.socio.id)}>
                        Abrir cobro y pedir tarjeta
                      </Button>
                    )}
                    {cobro && !cobro.error && cobro.estado !== 'sin_suscripcion' && (
                      <>
                        {!cobro.tiene_tarjeta && (
                          <Button size="sm" variant="outline" disabled={ocupado}
                                  onClick={() => reenviarEnlaceTarjeta(abierto.socio.id)}>
                            Reenviar enlace de tarjeta
                          </Button>
                        )}
                        <Button size="sm" variant="outline" disabled={ocupado}
                                onClick={() => cerrarCobro(abierto.socio.id)}>
                          Dejar de cobrarle
                        </Button>
                      </>
                    )}
                    {cobro?.error && (
                      <Button size="sm" variant="outline" disabled={ocupado}
                              onClick={() => abrirCobro(abierto.socio.id)}>
                        Reintentar abrir cobro
                      </Button>
                    )}
                  </div>
                </div>

                {cobro === null && (
                  <p className="text-sm text-slate-500 mt-2">Consultando a Stripe…</p>
                )}
                {cobro?.error && (
                  <p className="text-sm text-rose-700 mt-2">
                    No se pudo consultar: {cobro.error}
                  </p>
                )}
                {cobro?.aviso_prioridad && (
                  <div className="mt-2 text-sm text-indigo-900 bg-white/80 border border-indigo-200 rounded p-2">
                    {cobro.aviso_prioridad}
                  </div>
                )}
                {cobro && !cobro.error && (
                  <div className="mt-2 space-y-1 text-sm">
                    <div className="flex items-center gap-2">
                      {cobro.tiene_tarjeta
                        ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        : <AlertTriangle className="w-4 h-4 text-amber-600" />}
                      <span>
                        {cobro.estado === 'sin_suscripcion'
                          ? 'Sin suscripción todavía. Ábrela y el partner registra la tarjeta en su portal.'
                          : `Estado en Stripe: ${cobro.estado}`}
                      </span>
                    </div>
                    <div className="text-slate-600">
                      {cobro.tiene_tarjeta
                        ? `Tarjeta puesta: ${cobro.tarjeta?.marca || ''} ••••${cobro.tarjeta?.ultimos4 || ''}`
                        : 'SIN TARJETA. Él la pone en Portal del socio → forma de pago. No uses «Registrar pago» para fingir el cobro.'}
                    </div>
                    <div className="text-slate-600">
                      Se le cobran <b>{cobro.licencias_que_se_cobran}</b> licencia(s) ·
                      le factura <b>{cobro.empresa_que_le_factura === 'stripe_mx'
                        ? 'la SAPI (México, con CFDI)' : 'la LLC (resto del mundo, con invoice)'}</b>
                    </div>
                    {cobro.ultimo_error && (
                      <div className="text-rose-700">{cobro.ultimo_error}</div>
                    )}
                    {cobro.no_se_pudo_preguntar_a_stripe && (
                      <div className="text-amber-800">
                        No se pudo preguntar a Stripe ahora mismo, así que esto puede
                        estar desactualizado: {cobro.no_se_pudo_preguntar_a_stripe}
                      </div>
                    )}
                    <p className="text-xs text-slate-500 pt-1">
                      Si da de alta un cliente hoy, <b>no se le cobra hoy</b> en Stripe:
                      la cantidad sube y entra en el recibo del mes siguiente.
                    </p>
                  </div>
                )}
              </div>
            )}

            <BolsaDeMensajesDelSocio
              mensajes={mensajes} razon={razon} ocupado={ocupado}
              onVender={(b) => venderBloque(abierto.socio.id, b)} />

            {abierto.socio.tipo === 'partner' && (
              <div className={`${TARJETA} ${abierto.socio.paga_el_minimo ? 'border-amber-300 bg-amber-50/50' : ''}`}>
                <div className="text-sm font-medium mb-1">2. Resumen del trato (licencias)</div>
                <div className="text-sm">
                  Modelo <b>{abierto.socio.modelo_precio === 'escalonado' ? 'escalonado' : 'fijo'}</b>
                  {' · '}tiene <b>{abierto.socio.licencias_activas}</b> licencia(s) activa(s)
                  y pactó un mínimo de <b>{abierto.socio.minimo}</b>.
                </div>
                <div className="text-lg font-semibold mt-1 tabular-nums">
                  Se le cobran {abierto.socio.se_le_cobrarian} × {abierto.socio.precio_licencia}
                  {' '}= {abierto.socio.total_del_mes} {abierto.socio.moneda}
                <div className="mt-3 flex flex-wrap items-end gap-2" key={(abierto.socio.moneda || "") + "|" + (abierto.socio.forma_de_cobro || abierto.socio.cuenta_stripe || "")}>
                  <div>
                    <label className="text-[11px] text-slate-500 block">Moneda que le cobramos</label>
                    <select defaultValue={abierto.socio.moneda || 'USD'}
                            id="socio-moneda-ficha"
                            className="h-8 text-sm border rounded-md px-2 bg-white"
                            data-testid="socio-moneda-ficha">
                      <option value="MXN">MXN — pesos</option>
                      <option value="USD">USD — dólares</option>
                      <option value="EUR">EUR — euros</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-500 block">Cuenta Stripe donde cae</label>
                    <select defaultValue={abierto.socio.forma_de_cobro || abierto.socio.cuenta_stripe || ''}
                            id="socio-cuenta-ficha"
                            className="h-8 text-sm border rounded-md px-2 bg-white max-w-[280px]"
                            data-testid="socio-cuenta-ficha">
                      <option value="stripe_mx">Stripe México (SAPI) — CFDI</option>
                      <option value="stripe_intl">Stripe internacional (LLC) — invoice</option>
                    </select>
                  </div>
                  <Button size="sm" disabled={ocupado}
                          onClick={async () => {
                            const mon = document.getElementById('socio-moneda-ficha')?.value;
                            const cuenta = document.getElementById('socio-cuenta-ficha')?.value;
                            setOcupado(true);
                            try {
                              const { data } = await api.patch(
                                `/admin/socios/${abierto.socio.id}/cobro`,
                                { moneda: mon, forma_de_cobro: cuenta });
                              toast.success(data.aviso);
                              setAbierto((a) => a ? { ...a, socio: data.socio || a.socio } : a);
                              await cargar();
                            } catch (e) { toast.error(formatApiError(e)); }
                            finally { setOcupado(false); }
                          }}>
                    Guardar moneda y cuenta
                  </Button>
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  Puedes cobrar en dólares y que el dinero entre en Stripe México, o al revés.
                  Son dos decisiones tuyas.
                </p>
                </div>
                {abierto.socio.paga_el_minimo && (
                  <div className="text-sm text-amber-800 mt-1">
                    Está en el mínimo aunque tenga menos clientes (o ninguno).
                    Puede dar de alta sus negocios cuando quiera; el mínimo ya se cobra.
                  </div>
                )}
                {abierto.socio.aviso_clientes_propios && (
                  <p className="text-xs text-slate-600 mt-2">{abierto.socio.aviso_clientes_propios}</p>
                )}
                {abierto.socio.modelo_precio === 'escalonado' && Array.isArray(abierto.socio.escalones) && (
                  <div className="text-xs text-slate-600 mt-2">
                    Peldaños:{' '}
                    {abierto.socio.escalones.map((e) => (
                      `desde ${e.desde} → ${e.precio}`
                    )).join(' · ')}
                  </div>
                )}
                <div className="flex items-end gap-2 mt-3">
                  <div>
                    <label className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                      Mes a cobrar (solo si NO usas Stripe)
                      <HelpTooltip text={'Cargo manual de respaldo (transferencia). El camino normal es la tarjeta arriba. El mes se cuenta al cierre, no lo de hoy.'} />
                    </label>
                    <Input value={periodo} onChange={(e) => setPeriodo(e.target.value)}
                           className="w-32" placeholder="2026-08" />
                  </div>
                  <Button size="sm" variant="outline" disabled={ocupado}
                          onClick={() => cobrarLicencias(abierto.socio.id)}>
                    <CalendarClock className="w-4 h-4 mr-1" /> Generar cargo manual
                  </Button>
                </div>
              </div>
            )}

            <div>
              <h4 className="text-sm font-medium mb-1 flex items-center gap-1">
                Sus negocios (clientes propios)
                <HelpTooltip text={'Negocios del partner. Solo los activos cuentan como licencia. No son cuentas de CavernicolApp: las crea él o Super Admin en su nombre.'} />
              </h4>
              {abierto.socio.negocios.length === 0 && (
                <div className="text-sm text-slate-500 mb-2">
                  Todavía no tiene clientes. Puede darlos de alta abajo; el mínimo
                  de licencias se le cobra de todos modos.
                </div>
              )}
              {(abierto.socio.negocios || []).map((n) => (
                <div key={n.id} className="flex items-center justify-between gap-2 border rounded p-2 mb-1 text-sm">
                  <div className="min-w-0">
                    <span className="font-medium">{n.nombre}</span>
                    <span className="text-slate-500"> · {n.vertical}</span>
                    <span className={`ml-2 text-xs ${n.cuenta_como_licencia ? 'text-emerald-700' : 'text-slate-500'}`}>
                      {n.cuenta_como_licencia ? 'cuenta como licencia' : `no cuenta (${n.estado})`}
                    </span>
                  </div>
                  <Button size="sm" variant="outline" disabled={ocupado}
                          className="shrink-0 text-rose-700 border-rose-200"
                          onClick={() => desasignarNegocio(n.id, abierto.socio.id, n.nombre)}>
                    Quitar (venta directa)
                  </Button>
                </div>
              ))}
              <p className="text-xs text-slate-500 mt-1">
                «Quitar» no borra el negocio: lo recuperas como venta directa de CavernicolApp
                y el partner deja de pagar esa licencia. Por impago de 2 meses el proceso
                propone traspaso masivo (autorizas con motivo y se cancela su Stripe).
              </p>
            </div>

            {abierto.socio.tipo === 'partner' && (
              <div className={`${TARJETA} border-emerald-200 bg-emerald-50/30`} data-testid="alta-cliente-partner">
                <h4 className="text-sm font-medium mb-2 flex items-center gap-1">
                  <Plus className="w-4 h-4" /> Dar de alta un cliente suyo
                  <HelpTooltip text={'Crea un negocio NUEVO bajo este partner. No reasigna cuentas de CavernicolApp. El partner podrá comprar después paquetes de mensajes/voz para ese tenant.'} />
                </h4>
                <div className="grid md:grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-slate-500">Nombre del negocio</label>
                    <Input value={clienteNuevo.name}
                           onChange={(e) => setClienteNuevo({ ...clienteNuevo, name: e.target.value })}
                           placeholder="Salón del cliente" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Vertical</label>
                    <select value={clienteNuevo.vertical}
                            onChange={(e) => setClienteNuevo({ ...clienteNuevo, vertical: e.target.value })}
                            className="w-full px-3 py-2 text-sm border rounded-md">
                      {VERTICALES_CLIENTE.map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Dueño (nombre, opcional)</label>
                    <Input value={clienteNuevo.owner_name}
                           onChange={(e) => setClienteNuevo({ ...clienteNuevo, owner_name: e.target.value })}
                           placeholder="María López" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Dueño (correo, opcional)</label>
                    <Input value={clienteNuevo.owner_email}
                           onChange={(e) => setClienteNuevo({ ...clienteNuevo, owner_email: e.target.value })}
                           placeholder="maria@cliente.com" />
                  </div>
                </div>
                <Button className="mt-3" size="sm" disabled={ocupado}
                        onClick={() => altaClientePartner(abierto.socio.id)}>
                  Crear cliente del partner
                </Button>
              </div>
            )}

            <div>
              <h4 className="text-sm font-medium mb-1 flex items-center gap-1 text-slate-600">
                <Building2 className="w-4 h-4" /> Excepción: reasignar negocio sin dueño
                <HelpTooltip text={'Solo para casos raros (migración). Un partner no debe tomar cuentas de CavernicolApp: da de alta clientes nuevos arriba. Las Corp nunca aparecen aquí.'} />
              </h4>
              {disponibles.length === 0 ? (
                <p className="text-sm text-slate-500">
                  No hay negocios huérfanos (sin socio ni vendedor, y no Corp).
                  El camino normal es crear un cliente nuevo del partner.
                </p>
              ) : (
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {disponibles.map((n) => (
                    <div key={n.id} className="flex items-center justify-between border rounded p-2 text-sm">
                      <span>{n.nombre} <span className="text-slate-500">· {n.vertical}</span></span>
                      <Button size="sm" variant="outline" disabled={ocupado}
                              onClick={() => asignar(n.id, abierto.socio.id)}>
                        Asignar
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {(abierto.cargos || []).length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-1 flex items-center gap-1">
                  Cargos de licencias
                  <HelpTooltip text={'Lo que se le ha facturado mes a mes. «Registrar pago» solo deja constancia de que ya te pagó: no cobra nada por sí solo ni mueve dinero.'} />
                </h4>
                <table className="w-full text-sm border rounded">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="text-left px-2 py-1">Mes</th>
                      <th className="text-right px-2 py-1">Activas</th>
                      <th className="text-right px-2 py-1">Mínimo</th>
                      <th className="text-right px-2 py-1">Cobradas</th>
                      <th className="text-right px-2 py-1">Total</th>
                      <th className="text-right px-2 py-1">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {abierto.cargos.map((c) => (
                      <tr key={c.id} className="border-t">
                        <td className="px-2 py-1">{c.periodo}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{c.activas}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{c.minimo}</td>
                        <td className="px-2 py-1 text-right tabular-nums font-medium">{c.cobradas}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{c.total} {c.moneda}</td>
                        <td className="px-2 py-1 text-right space-x-1">
                          {c.estado === 'pending' ? (
                            <Button size="sm" variant="outline" disabled={ocupado}
                                    onClick={() => pagarCargo(c, abierto.socio.id)}>
                              Registrar pago manual
                            </Button>
                          ) : null}
                          {c.estado === 'paid' && (c.origen === 'manual' || !c.origen) ? (
                            <Button size="sm" variant="ghost" disabled={ocupado}
                                    className="text-rose-700"
                                    onClick={() => deshacerPagoManual(c, abierto.socio.id)}>
                              Deshacer pago
                            </Button>
                          ) : null}
                          {c.estado !== 'pending' && !(c.estado === 'paid' && (c.origen === 'manual' || !c.origen))
                            ? c.estado
                            : (c.estado === 'paid' ? <span className="text-xs text-slate-500">pagado</span> : null)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-xs text-slate-500 mt-1">
                  Las tres cifras se guardan por separado a propósito: así el socio
                  puede ver por qué se le cobró eso. El precio queda congelado en el
                  cargo — renegociar mañana no reescribe los meses ya emitidos.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {vendedores.length > 0 && (
        <p className="text-xs text-slate-500">
          Para atribuirle un negocio a un vendedor de casa (20% de la primera venta,
          5% de cada mes siguiente), hazlo desde la ficha del negocio en Tenants.
        </p>
      )}
    </div>
  );
}
